import classNames from 'classnames';
import {
  Dispatch,
  MouseEvent,
  MouseEventHandler,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DropzoneRootProps } from 'react-dropzone/.';

import { useAppDispatch } from 'app/hooks';
import FileUpload from 'components/files/FileUpload';
import {
  doFormMsg,
  FormMessage,
  FormMsg,
  NoFormMsg,
} from 'components/forms/FormMessage';
import TextAreaInput from 'components/forms/TextAreaInput';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { useModal } from 'components/modal/ModalContext';
import DropMenu from 'components/navigation/DropMenu';
import {
  SortableContainer,
  SortableInfo,
  SortableListInfo,
  SortableListType,
} from 'components/sortables/Sortable';
import { IFile, IFileCreate } from 'features/files/fileAPI';
import {
  AcceptedMimeType,
  allFileTypes,
  FolderLocation,
  getImageTypeInfo,
  getStorageFolderName,
  patchFileAsync,
  postFileAsync,
} from 'features/files/fileSlice';
import { IFolder } from 'features/folders/folderAPI';
import {
  findFolder,
  patchFolderAsync,
  postFolderAsync,
  validDestinationFolder,
} from 'features/folders/folderSlice';
import { AnyIdObject, AnyOrderObject } from 'helpers/objects';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSite } from 'selectors/siteSelectors';
import { useUser } from 'selectors/userSelectors';
import CreateFolderForm from './CreateFolderForm';
import {
  LibraryFolderProvider,
  useLibraryFolderContext,
} from './FolderContext';
import LibraryFolder from './LibraryFolder';

import './Library.scss';

type LibraryProps = {
  className?: string;
};

export default function Library(props: LibraryProps) {
  const { className } = props;
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewIcon, setPreviewIcon] = useState<IconSymbol | null>(null);
  const [previewMimeType, setPreviewMimeType] = useState<AcceptedMimeType>(
    AcceptedMimeType.jpg,
  );
  const [file, setFile] = useState<File | undefined>(undefined);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const uploadTrigger = useRef<MouseEventHandler | undefined>(undefined);
  const uploadInputProps = useRef<() => DropzoneRootProps>(undefined);
  const scrollableRef = useRef<HTMLDivElement>(null!);

  const dispatch = useAppDispatch();
  const { user } = useUser();
  const { library } = useSite();
  const [rootFolder, setRootFolder] = useState<IFolder | null>(null);

  useEffect(() => {
    if (!library) return;
    console.log('resetting root folder');
    setRootFolder(library.rootFolder);
  }, [library]);

  const reorderLibrary = useCallback(
    async (
      sortableInfo: SortableInfo,
      sourceItem: AnyOrderObject,
      sourceList: SortableListInfo,
      destinationItem?: AnyOrderObject,
      destinationList?: SortableListInfo,
      copy?: boolean,
    ) => {
      if (!destinationItem && !destinationList) return;
      if ((sourceItem as any).hasOwnProperty('folderName')) {
        // It's a folder
        const folder = findFolder(sourceItem.id, library);
        if (library && folder && destinationList) {
          // The new parent is either the targeted item or the rootFolder
          const newParent = destinationItem || findFolder(null, library);
          if (newParent && validDestinationFolder(folder.id!, newParent)) {
            const data: IFolder = { ...folder, parentFolder: newParent };
            dispatch(patchFolderAsync({ data })).then(
              (response: { payload: any }) => {
                switch (response.payload?.response.status) {
                  case 200:
                  case 201:
                    break;
                  case 500:
                  default:
                    // TODO: need a better action here, possibly force reload.
                    console.log(
                      'Folder not updated due to internal error. Please reload the page.',
                    );
                    break;
                }
              },
            );
          }
        }
      } else if ((sourceItem as any).hasOwnProperty('fileName')) {
        // It's a file
        const file: IFile = sourceItem as IFile;
        if (library && file && destinationList) {
          // The new parent is either the targeted item or the rootFolder
          const newParent = destinationItem || findFolder(null, library);
          if (newParent) {
            const data: IFile = { ...file, folder: newParent };
            dispatch(
              patchFileAsync({
                data,
                fromFolder: file.folder || undefined,
              }),
            ).then((response: { payload: any }) => {
              switch (response.payload?.response.status) {
                case 200:
                case 201:
                  break;
                case 500:
                default:
                  // TODO: need a better action here, possibly force reload.
                  console.log(
                    'File not updated due to internal error. Please reload the page.',
                  );
                  break;
              }
            });
          }
        }
      }
    },
    [dispatch, library],
  );

  if (!library) return null;

  const listInfo: SortableListInfo = {
    id: 1, // only one library, so id doesn't matter
    listType: SortableListType.Folders,
    // items: sortByOrder(list?.listItems as AnyOrderObject[]),
    items: library.allFolders as AnyIdObject[],
  };

  return (
    <div className={classNames('static_page library', className)}>
      <div className="page_container">
        <div className="page_content" ref={scrollableRef}>
          <LibraryFolderProvider>
            <FileUpload
              types={allFileTypes}
              currentSrc={null}
              previewSrc={previewSrc}
              defaultImg={null}
              setPreviewSrc={setPreviewSrc}
              previewIcon={previewIcon}
              setPreviewIcon={setPreviewIcon}
              noPreview={true}
              previewMimeType={previewMimeType}
              setPreviewMimeType={setPreviewMimeType}
              showMsg={(m) => doFormMsg(m, setFormMsg)}
              setFile={setFile}
              withTrigger={true}
              onDelete={() => {}}
              className={'library'}
              // renderTrigger={(trigger) => (uploadTrigger.current = trigger)}
              renderTrigger={(trigger, getInputProps) => {
                uploadTrigger.current = trigger;
                uploadInputProps.current = getInputProps;
              }}
            >
              <div className="library_content">
                <section className="page_header">
                  <h1>
                    <FormattedMessage id="LIBRARY.LIBRARY" />
                  </h1>
                </section>
                {rootFolder ? (
                  <SortableContainer reorder={reorderLibrary}>
                    <LibraryFolder folder={rootFolder} listInfo={listInfo} />
                  </SortableContainer>
                ) : (
                  <div>
                    <FormattedMessage id="LIBRARY.FOLDERS.NO_ROOT_FOLDER" />
                  </div>
                )}
              </div>
              <div className="tail_container">
                <LibraryBlockTail
                  previewSrc={previewSrc}
                  setPreviewSrc={setPreviewSrc}
                  previewIcon={previewIcon}
                  setPreviewIcon={setPreviewIcon}
                  previewMimeType={previewMimeType}
                  formMsg={formMsg}
                  setFormMsg={setFormMsg}
                  file={file}
                  setFile={setFile}
                  uploadTrigger={uploadTrigger}
                  uploadInputProps={uploadInputProps}
                />
              </div>
            </FileUpload>
          </LibraryFolderProvider>
        </div>
      </div>
    </div>
  );
}

type LibraryTailProps = {
  previewSrc: string | null;
  setPreviewSrc: Dispatch<SetStateAction<string | null>>;
  previewIcon: IconSymbol | null;
  setPreviewIcon: Dispatch<SetStateAction<IconSymbol | null>>;
  previewMimeType: AcceptedMimeType;
  formMsg: FormMsg;
  setFormMsg: Dispatch<SetStateAction<FormMsg>>;
  file: File | undefined;
  setFile: Dispatch<SetStateAction<File | undefined>>;
  uploadTrigger: RefObject<MouseEventHandler | undefined>;
  uploadInputProps: RefObject<(() => DropzoneRootProps) | undefined>;
};

export function LibraryBlockTail(props: LibraryTailProps) {
  const {
    previewSrc,
    setPreviewSrc,
    previewIcon,
    setPreviewIcon,
    previewMimeType,
    formMsg,
    setFormMsg,
    file,
    setFile,
    uploadTrigger,
    uploadInputProps,
  } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { user } = useUser();
  const { openModal } = useModal();
  const { selectedFolderId } = useLibraryFolderContext();
  const { library } = useSite();

  const [description, setDescription] = useState<string>('');
  const [showSend, setShowSend] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (previewSrc) {
      const imgType = getImageTypeInfo(previewMimeType);
      if (imgType) {
        const image = new Image();
        image.onload = () => {
          setSize({ width: image.width, height: image.height });
        };
        image.src = previewSrc;
      } else {
        setSize({ width: 48, height: 48 });
      }
    }
  }, [previewMimeType, previewSrc]);

  function handleDescriptionChange(input: string, hasText: boolean) {
    setShowSend(hasText);
    setDescription(input);
  }

  function submitFile(e?: React.SyntheticEvent) {
    if (!description) {
      setFormMsg({
        message: intl.formatMessage({ id: 'FILES.NO_DESCRIPTION' }),
        success: false,
        timeout: 1500,
      });
      return;
    }
    if (!file) {
      // no file posted?
      setFormMsg({
        message: intl.formatMessage({ id: 'FILES.NO_FILE' }),
        success: false,
        timeout: 1500,
      });
      return;
    } else {
      // file posted
      const forProject = e && 'altKey' in e && (e as MouseEvent).altKey;
      const data: IFileCreate = {
        folder: selectedFolderId || library?.rootFolder?.id!,
        description: description,
        uploadTime: new Date(),
        storage: getStorageFolderName(FolderLocation.Library),
        size: file.size,
        mimeType: file.type,
      };
      dispatch(
        postFileAsync({
          data: data,
          author: user,
          file: file,
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 201:
            break;
          default:
            console.log(
              response,
              response?.payload.response.status,
              response?.payload.response.data?.message,
            );
        }
        setDescription('');
        setShowSend(false);
        setPreviewSrc(null);
        setPreviewIcon(null);
        setFile(undefined);
      });
    }
  }

  function createFolder(e: MouseEvent) {
    openModal(<CreateFolderForm onSubmit={submitFolder} />);
  }

  async function submitFolder(name: string): Promise<number> {
    return dispatch(
      postFolderAsync({
        data: {
          folderName: name,
          parentFolder: selectedFolderId || library?.rootFolder?.id,
        },
      }),
    )
      .then((response: any) => {
        switch (response.payload?.response.status) {
          case 201:
            break;
          default:
            console.log(
              response,
              response?.payload.response.status,
              response?.payload.response.data?.message,
            );
        }
        return response.payload?.response.status;
      })
      .catch((error) => {
        return 500;
      });
  }

  function removeImage(e: MouseEvent) {
    e.stopPropagation();
    setPreviewSrc(null);
    setPreviewIcon(null);
    setFile(undefined);
  }

  const inputProps = uploadInputProps.current ? uploadInputProps.current() : {};
  return user ? (
    <>
      <div className="dropzone_container">
        {previewSrc ? (
          <div
            className="preview_image"
            style={{
              backgroundImage: `url("${previewSrc}")`,
              height: size.height,
              width: size.width,
            }}
          />
        ) : previewIcon ? (
          <Icon
            symbol={previewIcon ?? IconSymbol.mime_unknown}
            size={32}
            style={{ margin: 'auto' }}
          />
        ) : null}
        {file?.name ? (
          <div className="file_name" title={file.name}>
            {file.name}
          </div>
        ) : null}
        {formMsg ? (
          <FormMessage
            message={formMsg.message}
            success={formMsg.success}
            timeout={5000}
            setter={setFormMsg}
          />
        ) : null}
        <input {...inputProps} />
        {previewSrc || previewIcon ? (
          <div className="dropzone_icons">
            <Icon
              symbol={IconSymbol.pencil}
              onClick={(e) => {
                uploadTrigger.current && uploadTrigger.current(e);
              }}
              className="dropzone_trigger"
              size={20}
            />
            <Icon
              symbol={IconSymbol.bin}
              className="dropzone_bin"
              onClick={removeImage}
              size={20}
            />
          </div>
        ) : null}
      </div>
      <div className="msg_tail">
        <DropMenu right={true} up={true} shift={8}>
          <DropMenu.Trigger>
            <Icon symbol={IconSymbol.plus} className="msg_input_plus" />
          </DropMenu.Trigger>
          <DropMenu.Items>
            <div
              className="menu_item"
              onClick={(e) => {
                uploadTrigger.current && uploadTrigger.current(e);
              }}
            >
              <FormattedMessage id="LIBRARY.UPLOAD.DOCUMENT" />
              <Icon symbol={IconSymbol.mime_general} size={24} />
            </div>
            <div className="menu_item" onClick={createFolder}>
              <FormattedMessage id="LIBRARY.FOLDERS.CREATE" />
              <Icon symbol={IconSymbol.folderAdd} size={24} />
            </div>
          </DropMenu.Items>
        </DropMenu>
        <div className="msg_input">
          <TextAreaInput
            onChange={handleDescriptionChange}
            onSubmit={(s, b, e) => submitFile(e)}
            placeholder={intl.formatMessage({
              id:
                // forProject
                // ? 'FILES.DESCRIPTION_PROJECT'
                // :
                'FILES.DESCRIPTION_DIALOGUE',
            })}
            value={description}
            escapeClears={false}
          />
        </div>
        {showSend ? (
          <Icon
            symbol={IconSymbol.upload}
            size={28}
            className="msg_input_send"
            onClick={(e) => submitFile(e)}
            hintProps={{
              hint: intl.formatMessage({ id: 'LIBRARY.UPLOAD.SUBMIT' }),
            }}
          />
        ) : null}
      </div>
    </>
  ) : null;
}
